import React, { useEffect, useState } from 'react';
import axiosPrivate from 'config/axiosPrivate';
import { formatDateShort } from 'utils/DateUtils';
import { useTranslation } from 'react-i18next';

export default function ProjectAttributionCard({ order }) {
  const { t } = useTranslation();
  const [allocationStats, setAllocationStats] = useState({
    total: null,
    delivered: null,
    validated: null,
    deadline: null,
  });
  const [projectLines, setProjectLines] = useState([]);

  useEffect(() => {
    const shouldFetch =
      order?.id && (order?.type || '').toUpperCase() === 'PREMIUM';

    if (!shouldFetch) {
      setAllocationStats({
        total: null,
        delivered: null,
        validated: null,
        deadline: null,
      });
      setProjectLines([]);
      return;
    }

    const fetchProjectLines = async () => {
      try {
        const response = await axiosPrivate.get(`/project/line/${order.id}`);
        const data = response.data || {};
        const lines = Array.isArray(data.projectLineDtos)
          ? data.projectLineDtos
          : [];

        const totalTexts = lines.reduce(
          (sum, line) => sum + (Number(line.textAmount) || 0),
          0
        );

        const deliveredTexts = lines.reduce(
          (sum, line) => sum + (Number(line.deliveredTextsAmount) || 0),
          0
        );

        const validatedTexts = lines.reduce(
          (sum, line) => sum + (Number(line.validatedTextsAmount) || 0),
          0
        );

        const deadlines = lines
          .map((line) => line.deadline)
          .filter(Boolean)
          .map((d) => new Date(d));

        const latestDeadline =
          deadlines.length > 0
            ? new Date(Math.max.apply(null, deadlines))
            : null;

        setProjectLines(lines);
        setAllocationStats({
          total: totalTexts || null,
          delivered: deliveredTexts || 0,
          validated: validatedTexts || 0,
          deadline: latestDeadline,
        });
      } catch (error) {
        console.error('Error fetching project lines for attribution:', error);
        setAllocationStats((prev) => ({
          total: prev.total,
          delivered: prev.delivered,
          validated: prev.validated,
          deadline: prev.deadline,
        }));
        setProjectLines([]);
      }
    };

    fetchProjectLines();
  }, [order?.id, order?.type]);

  const total = allocationStats.total ?? (order?.textAmount || 0);
  const delivered = allocationStats.delivered ?? 0;
  const validated = allocationStats.validated ?? 0;

  const primaryWriter =
    Array.isArray(order?.writers) && order.writers.length > 0
      ? order.writers[0]
      : null;

  const getWriterDisplayName = (line) => {
    const dto = line?.userMailDTO;
    if (dto?.firstName != null || dto?.lastName != null) {
      return `${dto.firstName || ''} ${dto.lastName || ''}`.trim() || dto?.mail || '-';
    }
    if (dto?.mail) return dto.mail;
    const w = line?.writer || primaryWriter;
    if (w) {
      return w.name || `${w.firstName || ''} ${w.lastName || ''}`.trim() || '-';
    }
    return '-';
  };

  const sections = projectLines.length > 0
    ? projectLines
    : (primaryWriter || total > 0
        ? [{
            userMailDTO: primaryWriter?.userMailDTO || { mail: primaryWriter?.mail },
            writer: primaryWriter,
            textAmount: total,
            minWords: order?.minWords ?? order?.customMinWords,
            maxWords: order?.maxWords ?? order?.customMaxWords,
            deliveredTextsAmount: delivered,
            validatedTextsAmount: validated,
            deadline: allocationStats.deadline,
          }]
        : []);

  return (
    <div
      style={{
        borderRadius: 10,
        border: '1px solid #F3F4F6',
        padding: '10px 12px',
        backgroundColor: '#FFFFFF',
        boxShadow: '0 1px 2px rgba(15,23,42,0.04)',
      }}
    >
      {order?.type === 'PREMIUM' && sections.length > 0 && sections.map((line, idx) => {
        const lineTotal = Number(line.textAmount) || 0;
        const lineDelivered = Number(line.deliveredTextsAmount) || 0;
        const lineValidated = Number(line.validatedTextsAmount) || 0;
        const lineRemaining = Math.max(0, lineTotal - lineDelivered);
        const lineProgress =
          lineTotal > 0 ? Math.min(100, Math.max(0, (lineValidated / lineTotal) * 100)) : 0;

        return (
          <div
            key={line.writerProjectId || line.writer?.id || idx}
            style={{
              marginBottom: sections.length > 1 && idx < sections.length - 1 ? 16 : 0,
              paddingBottom: sections.length > 1 && idx < sections.length - 1 ? 12 : 0,
              borderBottom:
                sections.length > 1 && idx < sections.length - 1
                  ? '1px solid #F1F1F1'
                  : 'none',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 10,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                <img
                  src="/Images/img-red.png"
                  alt={t('Rédacteur')}
                  style={{
                    width: 32,
                    height: 32,
                    objectFit: 'cover',
                  }}
                />
                <span
                  style={{
                    fontSize: 14,
                    fontWeight: 600,
                    color: '#111827',
                  }}
                >
                  {getWriterDisplayName(line)}
                </span>
              </div>
            </div>


            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                marginBottom: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    borderRadius: 6,
                    backgroundColor: '#111827',
                    color: '#FFFFFF',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 12,
                    fontWeight: 600,
                  }}
                >
                  {lineRemaining}
                </div>
                <span style={{ fontSize: 13, fontWeight: 500, color: '#111827' }}>
                  {lineRemaining > 1 ? t('Textes') : t('Texte')}
                </span>
                <span
                  style={{
                    width: 1,
                    height: 14,
                    backgroundColor: '#E5E7EB',
                    margin: '0 4px',
                  }}
                />
                <span style={{ fontSize: 12, color: '#111827' }}>
                  {t('Livrés')} : {lineDelivered}
                </span>
                <span style={{ fontSize: 12, color: '#111827', marginLeft: 4 }}>
                  {t('Validés')} : {lineValidated}/{lineTotal}
                </span>
              </div>
            </div>

            <div
              style={{
                height: 3,
                borderRadius: 999,
                backgroundColor: '#F3F4F6',
                overflow: 'hidden',
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  width: `${lineProgress}%`,
                  height: '100%',
                  backgroundColor: '#D6A3FF',
                }}
              />
            </div>

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                fontSize: 12,
                color: '#9CA3AF',
              }}
            >
              <span>
                {t('{{count}} textes à livrer', { count: lineTotal })}
              </span>
              <span>
                {t('Date limite')} •{' '}
                {line.deadline
                  ? formatDateShort(
                      typeof line.deadline === 'string'
                        ? line.deadline
                        : line.deadline
                    )
                  : '-'}
              </span>
            </div>
          </div>
        );
      })}
    </div>
  );
}


